import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AlertEntity } from '@src/types/Alerts';
import { AppDefaultStateProps } from '@store/reducers';

import { Incident, IncidentMaxCellTMonitorProps } from '../types';
import {
  createIncidentFromAlert,
  deduplicateIncidents,
  updateIncidentWithRealTimeTemperature,
} from '../utils/incidentUtils';
import { useMqttSubscription } from '@hooks/mqtt/useMqttSubscription';

dayjs.extend(utc);

const MAX_CELL_T_ALERT_PATTERNS = ['MaxCellT_Rack_Incident'] as const;

export interface UseMaxCellTMonitorReturn {
  incidents: ReadonlyArray<Incident>;
  loading: boolean;
  error: null;
  lastDataUpdate: Date | null;
}

function isMaxCellTAlert(alert: AlertEntity): boolean {
  if (alert.config?.alertName) {
    const alertName = alert.config.alertName.toLowerCase();

    const matchesAlertName = MAX_CELL_T_ALERT_PATTERNS.some((pattern) =>
      alertName.includes(pattern.toLowerCase()),
    );

    if (matchesAlertName) {
      return true;
    }
  }

  return false;
}

function sortIncidents(incidents: Incident[]): Incident[] {
  return incidents.sort((a, b) => {
    const timeCompare =
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    if (timeCompare !== 0) {
      return timeCompare;
    }
    return b.maxCellT - a.maxCellT;
  });
}

function areIncidentsEqual(
  prev: ReadonlyArray<Incident>,
  next: ReadonlyArray<Incident>,
): boolean {
  if (prev.length !== next.length) {
    return false;
  }

  for (let i = 0; i < prev.length; i++) {
    const prevIncident = prev[i];
    const nextIncident = next[i];

    if (
      prevIncident.id !== nextIncident.id ||
      prevIncident.deviceName !== nextIncident.deviceName ||
      prevIncident.rackId !== nextIncident.rackId ||
      prevIncident.maxCellT !== nextIncident.maxCellT ||
      prevIncident.timestamp !== nextIncident.timestamp ||
      prevIncident.unitId !== nextIncident.unitId
    ) {
      return false;
    }
  }

  return true;
}

export function useMaxCellTMonitor(
  props: IncidentMaxCellTMonitorProps,
): UseMaxCellTMonitorReturn {
  const { siteMeta } = props;

  const [lastDataUpdate, setLastDataUpdate] = useState<Date | null>(null);
  const previousIncidentsRef = useRef<ReadonlyArray<Incident>>([]);

  const activeAlerts = useSelector(
    (state: AppDefaultStateProps) => state.alerts?.activeAlerts || [],
    (prevAlerts, nextAlerts) => {
      const prevMaxCellT = prevAlerts.filter(isMaxCellTAlert);
      const nextMaxCellT = nextAlerts.filter(isMaxCellTAlert);

      if (prevMaxCellT.length !== nextMaxCellT.length) {
        return false;
      }

      return prevMaxCellT.every((prevAlert, index) => {
        const nextAlert = nextMaxCellT[index];
        if (!nextAlert) {
          return false;
        }
        return (
          prevAlert.config?.id === nextAlert.config?.id &&
          prevAlert.config?.alertName === nextAlert.config?.alertName &&
          prevAlert.ts === nextAlert.ts &&
          prevAlert.value === nextAlert.value
        );
      });
    },
  );

  const incidents = useMemo(() => {
    if (!activeAlerts || activeAlerts.length === 0) {
      return [];
    }

    const maxCellTAlerts = activeAlerts.filter(isMaxCellTAlert);
    const newIncidents = maxCellTAlerts.map((alert) =>
      createIncidentFromAlert(alert, siteMeta),
    );

    const deduplicated = deduplicateIncidents(newIncidents);
    return sortIncidents(deduplicated);
  }, [activeAlerts, siteMeta]);

  const topics = useMemo(() => {
    return incidents
      .map((incident) => incident.mqttTopic)
      .filter(Boolean) as string[];
  }, [incidents]);

  const { data: realTimeTemperature, isLoading: mqttLoading } =
    useMqttSubscription(topics, {
      enabled: topics.length > 0,
    });

  const updatedIncidents = useMemo(
    (): Incident[] =>
      incidents.map((incident) =>
        updateIncidentWithRealTimeTemperature(incident, realTimeTemperature),
      ),
    [incidents, realTimeTemperature],
  );

  useEffect(() => {
    if (!areIncidentsEqual(previousIncidentsRef.current, incidents)) {
      previousIncidentsRef.current = incidents;
      if (incidents.length > 0) {
        setLastDataUpdate(new Date());
      }
    }
  }, [incidents]);

  const loading = topics.length > 0 ? mqttLoading : false;

  return {
    incidents: updatedIncidents,
    loading,
    error: null,
    lastDataUpdate,
  };
}
